import React, { useState } from 'react';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import Dialog from '@components/atoms/dialog/dialog';
import { NegotiationModal } from '@components/organisms/negotiation/negotiation-modal/negotiation-modal';
import { NegotiationDialogStyles } from '@constants/negotiation';
import { useNegotiation } from '@context/negotiation.context';
import { getOfferType, OfferStatus, OfferStatusId } from '@hooks/negotiations/useNegotiationStatus';
import { useGetNegotiationById } from '@hooks/negotiations/useNegotationQueries';
import logger from '@helpers/utils/logger/client';
import { CartContext } from '@context/cart.context';
import { Product } from '@interfaces/models/product';
import { useContextSelector } from 'use-context-selector';
import styles from '../negotiation-listings-card.module.scss';
import NegotiationListingsBuyNowButton from './negotiation-listings-card-cta-buy-now';
import NegotiationListingsRespondButton from './negotiation-listings-card-cta-respond';
import NegotiationListingsViewButton from './negotiation-listings-card-cta-view';

interface NegotiationListingsCardCtaProps {
  product?: Product; // not available for selling offers but required on buying offers for the cart
  negotiationId: string;
  offerStatus: OfferStatus;
  productId?: number; // required for selling offers but useless on buying offers
  pageType: 'buying-offers' | 'selling-offers';
  refetch?: () => void;
}

const NegotiationListingsCardCta: React.FC<NegotiationListingsCardCtaProps> = ({
  product,
  negotiationId,
  offerStatus,
  productId,
  pageType,
  refetch,
}) => {
  const [openNegotiationModal, setOpenNegotiationModal] = useState<boolean>(false);
  const { negotiation, setNegotiation } = useNegotiation();
  const addToShoppingCart = useContextSelector(CartContext, (v) => v.addToShoppingCart);
  const productsInCart = useContextSelector(CartContext, (v) => v.productsInCart);
  const isProductIdInCart = useContextSelector(CartContext, (v) => v.isProductIdInCart);
  const checkoutUrl = useContextSelector(CartContext, (v) => v.checkoutUrl);
  const { sendAnalyticEvent } = useAnalyticEvents('mmao');
  const resolvedProductId = product ? product.id : productId;

  const [shouldFetchNegotiation, setShouldFetchNegotiation] = useState(false);
  useGetNegotiationById({
    negotiationId: negotiationId,
    withEvents: true,
    enabled: shouldFetchNegotiation && !!negotiationId,
    onSuccessCallback: () => {
      setShouldFetchNegotiation(false);
    },
  });

  const closeNegotiationModal = () => {
    if (refetch) {
      const negoOfferStatusId = getOfferType(negotiation, true);
      if (negoOfferStatusId !== offerStatus.id) {
        refetch?.();
      }
    }
    setOpenNegotiationModal(false);
  };

  const launchModal = (sourceCta?: string) => {
    // reset negotiation in context before opening the modal
    setNegotiation(null);

    if (!negotiationId) {
      return;
    } else {
      const eventName = pageType === 'buying-offers' ? 'mmao_open_negotiating_area' : 'mmao_view_negotiating_area';
      sendAnalyticEvent(eventName, {
        product_id: resolvedProductId,
        label: sourceCta,
      });
      setShouldFetchNegotiation(true);
      setOpenNegotiationModal(true);
    }
  };

  const buyNow = async () => {
    try {
      sendAnalyticEvent('mmao_add_to_bag_buying_offers', {
        product_id: resolvedProductId,
        label: 'add_to_bag',
        property: '',
      });
      await addToShoppingCart(product, false);
    } catch (e) {
      logger.error(e, 'Error adding to cart - Buying Offers');
    } finally {
      window.location.href = checkoutUrl;
    }
  };

  const viewCart = () => {
    window.location.href = checkoutUrl;
  };

  const renderCTAButton = () => {
    if (offerStatus.id === OfferStatusId.accepted && !offerStatus.isSeller) {
      return (
        <NegotiationListingsBuyNowButton
          isProductInCart={isProductIdInCart(productsInCart, String(resolvedProductId))}
          onBuyNow={buyNow}
          onViewCart={viewCart}
        />
      );
    }

    if (offerStatus.id === OfferStatusId.received) {
      return <NegotiationListingsRespondButton onClick={() => launchModal('respond')} />;
    }

    return <NegotiationListingsViewButton onClick={() => launchModal('view')} />;
  };

  return (
    <div className={styles.negotiationListings__card__cta}>
      {renderCTAButton()}
      <Dialog
        closeDialog={closeNegotiationModal}
        isOpen={openNegotiationModal}
        hasCloseIcon
        styles={{
          content: NegotiationDialogStyles,
        }}
      >
        {(modalDialogTitleId) => (
          <NegotiationModal
            onClose={closeNegotiationModal}
            trackingFrom="TODO"
            modalDialogTitleId={modalDialogTitleId}
          />
        )}
      </Dialog>
    </div>
  );
};

export default NegotiationListingsCardCta;
